import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { SimulationMessage } from '../models/SimulationMessage';

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
    constructor(
        message: string,
        public status?: number,
        public errorType: 'network' | 'server' | 'timeout' | 'unknown' = 'unknown'
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

@Injectable({
    providedIn: 'root',
})
export class ChatbotService {
    private readonly REQUEST_TIMEOUT = 30000; // 30 seconds

    constructor(private storageService: StorageService) {}

    async *askQuestion(question: string): AsyncIterableIterator<string> {
        const url = `/api/v1/chatbot/dici`;
        const body: any = { question: question };

        const response = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                Authorization: 'Bearer ' + this.storageService.getToken(),
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error(`Response status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Failed to get reader from response body');
        }

        const decoder = new TextDecoder();

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            yield chunk;
        }
    }

    /**
     * Sends a message to the simulation API and returns a streaming response
     * @param moduleId The ID of the module
     * @param questionId The ID of the question
     * @param messages Array of messages to send to the API
     * @returns AsyncIterableIterator of response chunks
     * @throws ApiError with specific error details
     */
    async *sendSimulationMessage(moduleId: string, questionId: number, messages: SimulationMessage[]): AsyncIterableIterator<string> {
        const url = `/api/v1/questions/module/${moduleId}/question/${questionId}/simulation/`;
        const body = messages;

        // Create an AbortController for timeout handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);

        try {
            // Attempt to fetch with timeout
            const response = await fetch(url, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    Authorization: 'Bearer ' + this.storageService.getToken(),
                    'Content-Type': 'application/json',
                },
                signal: controller.signal
            });
            
            // Clear the timeout since we got a response
            clearTimeout(timeoutId);
            
            // Handle different HTTP error status codes with meaningful messages
            if (!response.ok) {
                let errorMessage = 'Une erreur est survenue lors de la communication avec le serveur.';
                let errorType: 'network' | 'server' | 'timeout' | 'unknown' = 'server';
                
                switch (response.status) {
                    case 400:
                        errorMessage = 'Requête invalide. Veuillez vérifier les données envoyées.';
                        break;
                    case 401:
                        errorMessage = 'Session expirée. Veuillez vous reconnecter.';
                        break;
                    case 403:
                        errorMessage = 'Accès refusé à cette ressource.';
                        break;
                    case 404:
                        errorMessage = 'La ressource demandée n\'existe pas.';
                        break;
                    case 429:
                        errorMessage = 'Trop de requêtes. Veuillez réessayer plus tard.';
                        break;
                    case 500:
                    case 502:
                    case 503:
                    case 504:
                        errorMessage = 'Le serveur a rencontré une erreur. Veuillez réessayer plus tard.';
                        break;
                }
                
                throw new ApiError(errorMessage, response.status, errorType);
            }

            // Check if response body is available
            if (!response.body) {
                throw new ApiError(
                    'Réponse vide du serveur.',
                    undefined,
                    'server'
                );
            }

            const reader = response.body.getReader();
            if (!reader) {
                throw new ApiError(
                    'Impossible de lire la réponse du serveur.',
                    undefined,
                    'server'
                );
            }

            const decoder = new TextDecoder();

            // Process the streaming response
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                yield chunk;
            }
        } catch (error: any) {
            // Clear the timeout to prevent memory leaks
            clearTimeout(timeoutId);
            
            console.error('Error in simulation API call:', error);
            
            // Handle different error types
            if (error.name === 'AbortError') {
                throw new ApiError(
                    'La requête a pris trop de temps. Veuillez vérifier votre connexion et réessayer.',
                    undefined,
                    'timeout'
                );
            } else if (error instanceof ApiError) {
                // Re-throw ApiError instances
                throw error;
            } else if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
                throw new ApiError(
                    'Problème de connexion réseau. Veuillez vérifier votre connexion internet.',
                    undefined,
                    'network'
                );
            } else {
                // For any other errors
                throw new ApiError(
                    'Une erreur inattendue est survenue. Veuillez réessayer.',
                    undefined,
                    'unknown'
                );
            }
        }
    }
}
